import React, { useContext, useMemo } from "react";

import styled from "styled-components";

import { IVR_CREATE_API } from "const/apiMethods";
import { Spacer } from "components/base/Spacer";
import { PermissionContext } from "../../../../App";

import { Form, Input, InputNumber, List, Loader, Panel, SelectPicker } from "rsuite";

const defCodes = [
    { "sip": 480 },
    { "sip": 486 },
    { "sip": 503 },
    { "sip": 403 },
    { "sip": 487 }
];


export default (
    {
        ivrTypes,
        editState,
        showNameEdit,
        forwardedRef,
        formValue,
        setFormValue,
        formModel,
        ivrSoundsList,
        loading,
        activeActionType = null
    }
) => {

    const permissions = useContext(PermissionContext);
    const paramsDisabled = useMemo(() => {
        return !permissions.includes(IVR_CREATE_API) || (!showNameEdit && !editState);
    }, [permissions, editState, showNameEdit]);

    const passwordRecognitionData = useMemo(() => {
        if (!formValue || !formValue["language_enum"]) return [];

        return formValue["language_enum"]?.map((language) => {
            return {
                value: language,
                label: language
            };
        });
    }, [formValue]);

    return (
        <StyledPanel shaded aria-disabled={paramsDisabled}>
            <BlockHeader>Parameters</BlockHeader>
            <Spacer size={20} />

            {loading &&
                <Loader backdrop size="sm" />
            }

            <Form
                ref={(ref) => forwardedRef(ref)}
                model={formModel}
                formValue={formValue}
                onChange={setFormValue}
            >

                {activeActionType === "RejectCall" &&
                    <>
                        <FormSection>
                            <FormSectionTitle></FormSectionTitle>
                            <FormRow>
                                <Form.ControlLabel>Percent common</Form.ControlLabel>
                                <ParameterNumberField
                                    name="percent"
                                    readOnly={paramsDisabled}
                                    min={0}
                                    max={100}
                                />
                                <Form.ControlLabel>%</Form.ControlLabel>
                            </FormRow>
                        </FormSection>

                        <FormSection>
                            <FormSectionTitle>SIP codes</FormSectionTitle>
                            <FormRow>
                                <List style={{ width: "100%" }}>
                                    {defCodes.map((item) => {
                                        return <List.Item>
                                            <StyledSipListItem>
                                                <div className="sip-code">
                                                    {item.sip}
                                                </div>
                                                <ParameterNumberField
                                                    name={`sip_${item.sip}`}
                                                    min={0}
                                                    max={100}
                                                    disabled={paramsDisabled}
                                                />
                                                <span>%</span>
                                            </StyledSipListItem>
                                        </List.Item>
                                    })}
                                </List>
                            </FormRow>
                        </FormSection>
                    </>
                }

                {activeActionType === "WaitBeforeConnect" &&
                    <>
                        <FormSection>
                            <FormSectionTitle>Delay before ringback <small>(0 - 60)</small></FormSectionTitle>
                            <FormRow>
                                <Form.ControlLabel>from</Form.ControlLabel>
                                <ParameterNumberField name="before_min" min={0} max={60} readOnly={paramsDisabled} />

                                <Form.ControlLabel>to</Form.ControlLabel>
                                <ParameterNumberField name="before_max" min={0} max={60} readOnly={paramsDisabled} />
                            </FormRow>
                        </FormSection>

                        <FormSection>
                            <FormSectionTitle>Ringback duration <small>(0 - 60)</small></FormSectionTitle>
                            <FormRow>
                                <Form.ControlLabel>from</Form.ControlLabel>
                                <ParameterNumberField name="ringing_min" min={0} max={60} readOnly={paramsDisabled} />

                                <Form.ControlLabel>to</Form.ControlLabel>
                                <ParameterNumberField name="ringing_max" min={0} max={60} readOnly={paramsDisabled} />
                            </FormRow>
                        </FormSection>
                    </>
                }

                {activeActionType === "Playback" &&
                    <>
                        <FormSection>
                            <FormSectionTitle>Playback <small>(1 - 7200)</small></FormSectionTitle>
                            <FormRow>
                                <Form.ControlLabel>from</Form.ControlLabel>
                                <ParameterNumberField name="playback_min" readOnly={paramsDisabled} />

                                <Form.ControlLabel>to</Form.ControlLabel>
                                <ParameterNumberField name="playback_max" readOnly={paramsDisabled} />
                            </FormRow>
                        </FormSection>
                        <FormSection>
                            <FormSectionTitle>Sound</FormSectionTitle>
                            <FormRow>
                                <ParameterSelectField
                                    data={ivrSoundsList}
                                    name="sound"
                                    labelKey="filename"
                                    valueKey="id"
                                    readOnly={paramsDisabled}
                                    placement="bottomEnd"
                                    preventOverflow
                                />
                            </FormRow>
                        </FormSection>
                    </>
                }

                {activeActionType === "PlaybackLoop" &&
                    <>
                        <FormSection>
                            <FormSectionTitle></FormSectionTitle>
                            <FormRow>
                                <Form.ControlLabel>Repeat</Form.ControlLabel>
                                <ParameterNumberField name="repetitions" readOnly={paramsDisabled} />
                            </FormRow>
                        </FormSection>
                        <FormSection>
                            <FormSectionTitle>Sound</FormSectionTitle>
                            <FormRow>
                                <ParameterSelectField
                                    data={ivrSoundsList}
                                    name="sound"
                                    labelKey="filename"
                                    valueKey="id"
                                    readOnly={paramsDisabled}
                                    placement="bottomEnd"
                                    preventOverflow
                                />
                            </FormRow>
                        </FormSection>
                    </>
                }

                {activeActionType === "PasswordRecognition" &&
                    <FormSection>
                        <FormSectionTitle></FormSectionTitle>
                        <FormRow>
                            <Form.ControlLabel>Language</Form.ControlLabel>
                            <ParameterSelectField
                                data={passwordRecognitionData}
                                name="language"
                                valueKey="value"
                                labelKey="label"
                                readOnly={paramsDisabled}
                            />
                        </FormRow>
                    </FormSection>
                }

                {activeActionType === "Radio" &&
                    <FormSection>
                        <FormSectionTitle>Radio params <small>(1 - 7200)</small></FormSectionTitle>
                        <FormRow>
                            <Form.ControlLabel>from</Form.ControlLabel>
                            <ParameterNumberField name="radio_min" readOnly={paramsDisabled} />

                            <Form.ControlLabel>to</Form.ControlLabel>
                            <ParameterNumberField name="radio_max" readOnly={paramsDisabled} />
                        </FormRow>
                    </FormSection>
                }

                {activeActionType === "Ringback" &&
                    <FormSection>
                        <FormSectionTitle>Ringbacktone params <small>(1 - 7200)</small></FormSectionTitle>
                        <FormRow>
                            <Form.ControlLabel>from</Form.ControlLabel>
                            <ParameterNumberField name="ringbacktone_min" readOnly={paramsDisabled} />

                            <Form.ControlLabel>to</Form.ControlLabel>
                            <ParameterNumberField name="ringbacktone_max" readOnly={paramsDisabled} />
                        </FormRow>
                    </FormSection>
                }

                {activeActionType === "SendDTMF" &&
                    <FormSection>
                        <FormSectionTitle></FormSectionTitle>
                        <FormRow>
                            <Form.ControlLabel>DTMF Signal</Form.ControlLabel>
                            <div style={{ maxWidth: "50px" }}>
                                <Form.Control errorPlacement="topEnd" accepter={Input} name="digit" readOnly={paramsDisabled} />
                            </div>
                        </FormRow>
                    </FormSection>
                }

                {activeActionType === "Silence" &&
                    <FormSection>
                        <FormSectionTitle>Silence params <small>(1 - 7200)</small></FormSectionTitle>
                        <FormRow>
                            <Form.ControlLabel>from</Form.ControlLabel>
                            <ParameterNumberField name="silence_min" readOnly={paramsDisabled} />

                            <Form.ControlLabel>to</Form.ControlLabel>
                            <ParameterNumberField name="silence_max" readOnly={paramsDisabled} />
                        </FormRow>
                    </FormSection>
                }

                {activeActionType === "Wait" &&
                    <FormSection>
                        <FormSectionTitle>Wait <small>(0 - 7200)</small></FormSectionTitle>
                        <FormRow>
                            <Form.ControlLabel>from</Form.ControlLabel>
                            <ParameterNumberField name="wait_min" min={0} readOnly={paramsDisabled} />

                            <Form.ControlLabel>to</Form.ControlLabel>
                            <ParameterNumberField name="wait_max" readOnly={paramsDisabled} />
                        </FormRow>
                    </FormSection>
                }

                {activeActionType === "ReadDTMF" &&
                    <FormSection>
                        <FormSectionTitle>Wait <small>(1 - 10)</small></FormSectionTitle>
                        <FormRow>
                            <ParameterNumberField name="read_wait" min={1} max={10} readOnly={paramsDisabled} />
                        </FormRow>
                    </FormSection>
                }

            </Form>
            <Spacer size={20} />
        </StyledPanel>
    );
};

const ParameterNumberField = (props) => (
    <div className="form-input" style={{ maxWidth: "90px" }}>
        <Form.Control
            accepter={InputNumber}
            errorPlacement="topEnd"
            min={1}
            max={7200}
            step={1}
            {...props}
        />
    </div>
);

const ParameterSelectField = (props) => (
    <Form.Control
        searchable={false}
        cleanable={false}
        errorPlacement="topEnd"
        style={{ width: "100%" }}
        accepter={SelectPicker}
        {...props}
    />
);

const BlockHeader = styled.h5`
    font-family: inherit;
    font-weight: 400;
    font-size: 18px;
    line-height: 21px;
    color: var(--color-brand1);
`;

const StyledPanel = styled(Panel)`
    min-height: 100% !important;
    overflow: visible !important;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;

    &[aria-disabled="true"] {
        background-color: #f5f5f5;
        box-shadow: 0 0 0 1px #e5e5ea;

        .rs-panel-body {
            opacity: 0.55;
        }

        .rs-input,
        .rs-input-number,
        .rs-picker-toggle {
            cursor: not-allowed;
        }
    }
`;

const FormSection = styled.div`
    & + * {
        padding-top: 20px;
    }
`;

const FormSectionTitle = styled.p`
    padding-bottom: 10px;
    font-weight: 500;
`;

const FormRow = styled.div`
    display: flex;
    align-items: center;

    .rs-list {
        overflow: visible
    }
    
    .rs-form-control-label {
        margin-right: 10px;
    }
    .form-input {
        padding-right: 15px;
    }
    
    & + * {
        padding-top: 20px;
    }
`;

const StyledSipListItem = styled.div`
    align-items: center;
    display: flex;
    width: "100%";

    .sip-code {
        font-weight: 600;
        width: "70px";
    }
`;
